import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import type { Decline } from '../../lib/api';

const KNOWN = ['violence', 'sexual', 'drugs', 'profanity', 'discrimination', 'horror', 'trademark', 'other'];
const THEME_MAX = 500;

type Proposal = { status: 'loading' } | { status: 'ready'; text: string } | { status: 'failed' };

interface Props {
  open: boolean;
  decline: Decline | null;
  /** The text that was declined — seeds the editable box each time the dialog opens. */
  theme: string;
  onClose: () => void;
  /** Insert the chosen text and resubmit. The caller owns the history + the submit. */
  onApply: (text: string) => void;
  /** Surface-specific rewrite call (anonymous endpoint vs. the account one). `hint` is the
   *  screener's reason or excerpt so the rewrite targets the flagged beat. */
  soften: (text: string, hint: string | null) => Promise<string>;
}

/**
 * Submit-time recovery for a content-screen decline (Kai, 2026-09-06: "just showing the
 * error is a dead end for most users"; 2026-09-12: a parent whose teddy-under-a-car idea
 * was declined resubmitted it four times and never touched the outline "Automatisch
 * anpassen" button). Now: the screener's own one-line reason, the offending phrase
 * highlighted, and a softened version requested THE MOMENT the dialog opens — the parent
 * chooses between "use the suggestion" (primary) and editing their own text with the
 * reason as a guide. The manual path is never disabled: a failed rewrite only shows a line.
 *
 * `data-no-swipe` on the content is load-bearing on the reader's closing slide: the
 * keyboard-nav hook listens on `window` and would turn the page (unmounting this dialog)
 * on ArrowLeft while a button has focus. Every button is `type="button"` because React
 * submit events bubble through portals into /try's form.
 */
export default function DeclineDialog({ open, decline, theme, onClose, onApply, soften }: Props) {
  const { t } = useTranslation();
  const [text, setText] = useState(theme);
  const [proposal, setProposal] = useState<Proposal>({ status: 'loading' });
  const run = useRef(0);

  useEffect(() => {
    if (!open || !decline) return;
    setText(theme);
    setProposal({ status: 'loading' });
    const id = ++run.current;
    const hint = decline.reason ?? decline.matched ?? null;
    soften(theme, hint)
      .then((softened) => {
        if (run.current !== id) return;
        const clean = (softened || '').trim().slice(0, THEME_MAX);
        setProposal(clean.length >= 3 ? { status: 'ready', text: clean } : { status: 'failed' });
      })
      .catch(() => { if (run.current === id) setProposal({ status: 'failed' }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, theme, decline]);

  if (!decline) return null;
  const category = KNOWN.includes(decline.category) ? decline.category : 'other';
  const matched = decline.matched && text.includes(decline.matched) ? decline.matched : null;
  const excerpt = matched ? splitAround(text, matched) : null;
  const canApply = text.trim().length >= 3;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md rounded-3xl max-h-[92dvh] overflow-y-auto" data-no-swipe="" data-testid="decline-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-left">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
            {t('create.declineHeading')}
          </DialogTitle>
          <DialogDescription className="text-left" data-testid="decline-reason">
            {decline.reason || t(`create.declineReason.${category}`)}
          </DialogDescription>
        </DialogHeader>

        {excerpt && (
          <p className="text-sm rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 leading-relaxed" data-testid="decline-excerpt">
            <span className="font-medium">{t('decline.found', { term: '' }).replace(/[„“"]+\s*$/, '')}</span>{' '}
            <span className="text-muted-foreground">{excerpt.before}</span>
            <mark className="bg-amber-200 rounded px-0.5 font-semibold" data-testid="decline-term">{matched}</mark>
            <span className="text-muted-foreground">{excerpt.after}</span>
          </p>
        )}

        <div className="rounded-2xl border-2 border-primary/30 bg-primary/5 px-3 py-2.5 space-y-2">
          <p className="text-sm font-semibold flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-primary" />
            {t('decline.proposal')}
          </p>
          {proposal.status === 'loading' && (
            <p className="text-sm text-muted-foreground flex items-center gap-2" data-testid="decline-proposing">
              <Loader2 className="w-4 h-4 animate-spin shrink-0" />
              {t('decline.proposing')}
            </p>
          )}
          {proposal.status === 'ready' && (
            <p className="text-sm leading-relaxed" data-testid="decline-proposal">{proposal.text}</p>
          )}
          {proposal.status === 'failed' && (
            <p className="text-sm text-muted-foreground" data-testid="decline-error">{t('decline.proposalFailed')}</p>
          )}
          <Button
            type="button"
            onClick={() => proposal.status === 'ready' && onApply(proposal.text)}
            disabled={proposal.status !== 'ready'}
            data-testid="decline-use-proposal"
            className="w-full"
          >
            {t('decline.useProposal')}
          </Button>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="decline-theme" className="text-sm text-muted-foreground">{t('decline.editLabel')}</label>
          <Textarea
            id="decline-theme"
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, THEME_MAX))}
            maxLength={THEME_MAX}
            rows={4}
            data-testid="decline-theme"
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end pt-1">
          <Button type="button" variant="outline" onClick={() => onApply(text.trim())} disabled={!canApply} data-testid="decline-apply">
            {t('decline.useMine')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose} data-testid="decline-cancel">
            {t('common.cancel')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** A short window of context around the matched term so the highlight reads in place. */
function splitAround(text: string, term: string, ctx = 60): { before: string; after: string } {
  const i = text.indexOf(term);
  const before = text.slice(Math.max(0, i - ctx), i);
  const after = text.slice(i + term.length, i + term.length + ctx);
  return {
    before: (i - ctx > 0 ? '…' : '') + before,
    after: after + (i + term.length + ctx < text.length ? '…' : ''),
  };
}
